import { useEffect } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';

const SITE_URL = 'https://www.amirassi.com';
const LOGO_URL = `${SITE_URL}/images/logo.png`;
const DEFAULT_IMAGE = 'https://storage.googleapis.com/gpt-engineer-file-uploads/XFut8vnPAEQaE2zpNAhpf8RTDNx2/social-images/social-1776587914206-%D7%A6%D7%99%D7%9C%D7%95%D7%9D_%D7%9E%D7%A1%D7%9A_2026-04-19_%D7%91-11.38.19.webp';

interface SEOHeadProps {
  title: { he: string; ar: string; en: string };
  description: { he: string; ar: string; en: string };
  path: string;
  type?: 'website' | 'article' | 'profile';
  image?: string;
  jsonLd?: Record<string, unknown>;
}

const SEOHead = ({ title, description, path, type = 'website', image, jsonLd }: SEOHeadProps) => {
  const { lang } = useLanguage();

  const brand = lang === 'he' ? 'אמיר עאסי' : lang === 'ar' ? 'أمير عاصي' : 'Amir Assi';
  const rawTitle = title[lang];
  const pageTitle = rawTitle.includes(brand) ? rawTitle : `${rawTitle} | ${brand}`;
  const pageDescription = description[lang];
  const canonicalUrl = `${SITE_URL}/${lang}${path}`;
  const ogImage = image || DEFAULT_IMAGE;

  useEffect(() => {
    // Title
    document.title = pageTitle;

    // Helper to set/create meta tag
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Standard meta
    setMeta('name', 'description', pageDescription);

    // Open Graph
    setMeta('property', 'og:title', pageTitle);
    setMeta('property', 'og:description', pageDescription);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:locale', lang === 'he' ? 'he_IL' : lang === 'ar' ? 'ar_SA' : 'en_US');
    setMeta('property', 'og:site_name', 'אמיר עאסי | Amir Assi');

    setMeta('property', 'og:image:alt', pageTitle);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');

    // Twitter
    setMeta('name', 'twitter:title', pageTitle);
    setMeta('name', 'twitter:description', pageDescription);
    setMeta('name', 'twitter:image', ogImage);
    setMeta('name', 'twitter:image:alt', pageTitle);
    setMeta('name', 'twitter:card', 'summary_large_image');


    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // Alternate language links
    const langs = ['he', 'ar', 'en'] as const;
    langs.forEach((l) => {
      const hreflang = l === 'he' ? 'he' : l === 'ar' ? 'ar' : 'en';
      let alt = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`) as HTMLLinkElement | null;
      if (!alt) {
        alt = document.createElement('link');
        alt.setAttribute('rel', 'alternate');
        alt.setAttribute('hreflang', hreflang);
        document.head.appendChild(alt);
      }
      alt.setAttribute('href', `${SITE_URL}/${l}${path}`);
    });

    // JSON-LD
    const existingScript = document.querySelector('script[data-seo-jsonld]');
    if (existingScript) existingScript.remove();

    const defaultJsonLd: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': type === 'profile' ? 'Person' : type === 'article' ? 'Article' : 'WebPage',
      name: pageTitle,
      description: pageDescription,
      url: canonicalUrl,
      image: ogImage,
      logo: LOGO_URL,
      ...(type === 'article' ? {
        headline: title[lang],
        author: { '@type': 'Person', name: 'Amir Assi', alternateName: 'אמיר עאסי' },
      } : {}),
      ...(type === 'profile' || path === '' || path === '/'
        ? {
            '@type': 'Person',
            name: 'Amir Assi',
            alternateName: 'אמיר עאסי',
            jobTitle: 'Strategic Consultant & Senior Lecturer',
            url: SITE_URL,
            image: ogImage,
            logo: LOGO_URL,
            sameAs: [
              'https://www.facebook.com/amir.assi.9',
              'https://www.instagram.com/amir_assi11/',
            ],
          }
        : {}),
      ...jsonLd,
    };

    const script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(defaultJsonLd);
    document.head.appendChild(script);

    return () => {
      const s = document.querySelector('script[data-seo-jsonld]');
      if (s) s.remove();
    };
  }, [pageTitle, pageDescription, canonicalUrl, ogImage, type, lang, jsonLd, path]);

  return null;
};

export default SEOHead;
